export function bootAffordance({ bootableAt, bootAfterSeconds, now, viewerCanBoot }: {
  bootableAt: string | null | undefined
  bootAfterSeconds: number
  now: number
  viewerCanBoot: boolean
}): 'none' | 'hint' | 'boot' {
  if (!bootableAt || !viewerCanBoot) return 'none'
  const deadline = Date.parse(bootableAt)
  if (!Number.isFinite(deadline)) return 'none'
  if (now >= deadline) return 'boot'
  return now >= deadline - bootAfterSeconds * 300 ? 'hint' : 'none'
}

export function shortBootDuration(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000))
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor(minutes % 1440 / 60)
  const mins = minutes % 60
  return days ? `${days}d ${hours}h` : hours ? `${hours}h ${mins}m` : `${mins}m`
}

type BootTargetPlayer = { userId: string; slot: number; isBot: boolean; displayName: string; vacation?: unknown }
type BootTargetState = {
  players: readonly string[]
  turnIndex: number
  eliminated: readonly string[]
  map: { territories: readonly { ownerId: string; fogged?: boolean }[] }
}

/** The one opponent for whom the viewer may see the overdue banner and badge. */
export function overdueBootTarget({ state, players, viewerUserId, bootableAt, bootAfterSeconds, now }: {
  state: BootTargetState | null
  players: readonly BootTargetPlayer[]
  viewerUserId?: string
  bootableAt?: string | null
  bootAfterSeconds: number
  now: number
}): BootTargetPlayer | null {
  if (!state || !players.some(player => player.userId === viewerUserId && !player.isBot)) return null
  const slot = state.players[state.turnIndex]
  if (!slot) return null
  const target = players.find(player => String(player.slot) === slot)
  if (!target || target.isBot || target.userId === viewerUserId || target.vacation || state.eliminated.includes(slot)) return null
  // Backstop for a turn holder who no longer holds anything. In fog of war the
  // viewer may see none of their territories, so rely on `eliminated` alone.
  const fogged = state.map.territories.some(territory => territory.fogged)
  if (!fogged && !state.map.territories.some(territory => territory.ownerId === slot)) return null
  return bootAffordance({ bootableAt, bootAfterSeconds, now, viewerCanBoot: true }) === 'boot' ? target : null
}
